import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Switch,
  Grid,
  Paper,
  Chip,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
  Alert,
  Snackbar,
  CardMedia,
  TextField,
  IconButton,
  Skeleton,
  Link,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Visibility as VisibilityIcon,
  MarkEmailRead as MarkEmailReadIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import MainLayout from '../../../../components/Layout/MainLayout';
import ContactManager, { ContactData } from '../../../../components/ContactManager/ContactManager';
import { TemplateChangeModal } from '../../components/TemplateChangeModal/TemplateChangeModal';
import httpClient from '../../../../utils/httpClient';
import { ApiErrorResponse, ApiSuccessResponse } from '../../../../utils/apiSchemas';
import { Alert as MuiAlert } from '@mui/material';
import { Tabs, Tab } from '@mui/material';
import { NotificationEmailTypeEnum } from '../../enums/NotificationEmailTypeEnum';
import { FeatureTypeEnum } from '../../enums/FeatureTypeEnum';
import { useAuth } from '../../../../contexts/AuthContext';
import defaultTemplateImage from '../../assets/no_image.gif';
import ApiConnectionAlert from '../../../../components/ApiConnectionAlert/ApiConnectionAlert';
import { useTranslation } from 'react-i18next';
import { SectorEnum } from '../../../../utils/SectorEnum';

  interface Feature {
    id: string;
    name: string;
    description: string;
    enabled: boolean;
    enableGlobally: boolean;
    enableByClientRaw: boolean | null;
    category: 'Federal' | 'Provincial';
    website: string;
    referenceUrl?: string;
    featureType: FeatureTypeEnum;
    notificationEmailType: NotificationEmailTypeEnum;
    dailySampleLimit: number;
    dailySampleUsed: number;
    customContacts?: ContactData;
    templates?: Template[];
    templateTags?: TemplateTag[];
    emBreve?: boolean;
    notifyTelegram?: boolean;
    // Fallback description when templates array is empty
    socialMediaDescription?: string;
  }

  interface Template {
    id: string;
    name: string;
    preview: string;
    thumbnail: string;
    originalImage: string;
    parametrizedImage: string;
    socialMediaLabel: string;
    guid?: string;
  }

  interface TemplateTag {
    name: string;
    description: string;
    exampleValue: string;
  }

  interface AddressEmailOption {
    email: string;
    name?: string;
    role?: string;
    sector?: number | null;
  }

const Features: React.FC = () => {
  const { isSuperUser, user } = useAuth();
  const { t } = useTranslation();
  // Data loaded from API only (no mocked defaults)
  const [features, setFeatures] = useState<Feature[]>([]);
  const [featuresLoadErrors, setFeaturesLoadErrors] = useState<string[]>([]);
  const [dispatchingSampleById, setDispatchingSampleById] = useState<Record<string, boolean>>({});
  const [sampleConfirmOpen, setSampleConfirmOpen] = useState(false);
  const [sampleConfirmFeature, setSampleConfirmFeature] = useState<Feature | null>(null);
  const [sampleConfirmLoading, setSampleConfirmLoading] = useState(false);
  const [sampleEmailOptions, setSampleEmailOptions] = useState<AddressEmailOption[]>([]);
  const [sampleSelectedEmails, setSampleSelectedEmails] = useState<string[]>([]);

  const [selectedFeature, setSelectedFeature] = useState<Feature | null>(null);
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('info');
  const [alertDuration, setAlertDuration] = useState<number>(3000);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [originalImageLoading, setOriginalImageLoading] = useState(true);
  const [paramImageLoading, setParamImageLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [currentTemplateIndex, setCurrentTemplateIndex] = useState(0);
  const [socialLabelText, setSocialLabelText] = useState('');
  const [templateChangeModalOpen, setTemplateChangeModalOpen] = useState(false);
  const [templateDialogTab, setTemplateDialogTab] = useState<number>(0);
  const [telegramGroupUrl, setTelegramGroupUrl] = useState<string | undefined>(undefined);
  const dialogPaperSx = { overflowX: 'auto' };
  const dialogContentSx = { overflowX: 'auto' };

  const renderSocialLabelInstructions = () => {
    const rows = selectedFeature?.templateTags || [];
    if (!rows.length) return null;
    const wrap = (children: React.ReactNode) => (
      <Box sx={{ mb: 2, p: 2, border: '1px dashed #ccc', borderRadius: 1, bgcolor: '#f9fafb', overflowX: 'auto' }}>
        {children}
      </Box>
    );

    return wrap(
      <Box sx={{ minWidth: 'fit-content' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
          Instruções de Template
        </Typography>
        <Table size="small" sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Descrição</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Nome da Tag</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('features_social_label_example_column_title')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, idx) => (
              <TableRow key={`instr-row-${idx}`}>
                <TableCell>{r.description}</TableCell>
                <TableCell><Box component="span" sx={{ fontWeight: 'bold' }}>{`##${r.name}##`}</Box></TableCell>
                <TableCell>{r.exampleValue}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  };

  const renderTemplateExamplesCard = () => {
    const ft = selectedFeature?.featureType;
    if (!ft) return null;
    const wrap = (children: React.ReactNode) => (
      <Box sx={{ mb: 2, p: 2, border: '1px dashed #ccc', borderRadius: 1, bgcolor: '#f9fafb', overflowX: 'auto' }}>
        {children}
      </Box>
    );

    const ExamplesTable: React.FC<{ rows: { howTo: string; result: string }[] }> = ({ rows }) => {
      const lineSep = '\n\n';
      const howToText = rows.map(r => r.howTo).join(lineSep);
      const resultText = rows.map(r => r.result).join(lineSep);
      const renderWithBoldTags = (text: string) => (
        <Box sx={{ whiteSpace: 'pre-wrap' }}>
          {text.split(/(##[A-Z_]+##)/g).map((part, i) =>
            /##[A-Z_]+##/.test(part)
              ? <Box key={i} component="span" sx={{ fontWeight: 'bold' }}>{part}</Box>
              : part
          )}
        </Box>
      );
      const renderResultWithBold = (text: string) => (
        <Box sx={{ whiteSpace: 'pre-wrap' }}>
          {text.split(/(##[A-Z_]+##|\b[A-Z][a-zA-Z]+(?: [A-Z][a-zA-Z]+)*)/g).map((part, i) => {
            const isTag = /##[A-Z_]+##/.test(part);
            const isExampleValue = !isTag && /\b[A-Z][a-zA-Z]+(?: [A-Z][a-zA-Z]+)*/.test(part);
            return isTag || isExampleValue
              ? <Box key={i} component="span" sx={{ fontWeight: 'bold' }}>{part}</Box>
              : part;
          })}
        </Box>
      );

      // Destacar apenas os valores que substituem as tags no resultado final
      const renderResultBoldByTags = (howTo: string, result: string) => {
        const tagRegex = /##[A-Z_]+##/g;
        const hasTags = tagRegex.test(howTo);
        if (!hasTags) {
          return <Box component="span" sx={{ whiteSpace: 'pre-wrap' }}>{result}</Box>;
        }

        // Monta um regex baseado no texto de "howTo" para capturar apenas os trechos que substituem as tags
        const escapeRegex = (s: string) => s.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
        const parts = howTo.split(tagRegex);
        const tags = howTo.match(tagRegex) || [];

        let pattern = '^';
        for (let i = 0; i < parts.length; i++) {
          pattern += escapeRegex(parts[i]);
          if (i < tags.length) {
            pattern += '(.+?)';
          }
        }
        pattern += '$';

        try {
          const re = new RegExp(pattern, 's');
          const m = re.exec(result);
          if (!m) {
            return <Box component="span" sx={{ whiteSpace: 'pre-wrap' }}>{result}</Box>;
          }

          const captures = m.slice(1);
          const nodes: React.ReactNode[] = [];
          let capIdx = 0;
          for (let i = 0; i < parts.length; i++) {
            if (parts[i]) nodes.push(parts[i]);
            if (i < tags.length) {
              const cap = captures[capIdx++] || '';
              nodes.push(
                <Box key={`cap-${i}`} component="span" sx={{ fontWeight: 'bold' }}>{cap}</Box>
              );
            }
          }

          return <Box component="span" sx={{ whiteSpace: 'pre-wrap' }}>{nodes}</Box>;
        } catch {
          return <Box component="span" sx={{ whiteSpace: 'pre-wrap' }}>{result}</Box>;
        }
      };
      return (
        <Grid container spacing={1} alignItems="center" justifyContent="center" sx={{ flexWrap: 'nowrap', overflowX: 'auto', minWidth: 'fit-content' }}>
          <Grid component={'div' as React.ElementType} item xs={12} sm={5} md={4}>
            <Table size="small" sx={{ minWidth: 360 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('features_examples_howto_title')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{renderWithBoldTags(howToText)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Grid>

          <Grid component={'div' as React.ElementType} item xs={12} md={1}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <ChevronRightIcon sx={{ fontSize: '3rem' }} aria-label="Arrow pointing to the right" />
            </Box>
          </Grid>

          <Grid component={'div' as React.ElementType} item xs={12} sm={5} md={4}>
            <Table size="small" sx={{ minWidth: 360 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('features_examples_result_title')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                   <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                     <Box sx={{ whiteSpace: 'pre-wrap' }}>
                       {rows.map((r, idx) => (
                         <React.Fragment key={`result-frag-${idx}`}>
                           {renderResultBoldByTags(r.howTo, r.result)}
                           {idx < rows.length - 1 ? lineSep : null}
                         </React.Fragment>
                       ))}
                     </Box>
                   </TableCell>
                 </TableRow>
              </TableBody>
            </Table>
          </Grid>
        </Grid>
      );    };

    switch (ft) {
      case FeatureTypeEnum.CANADA_CA_IRCC_ROUNDS_OF_INVITATIONS: {
        const rows = [
          { howTo: '🎯 Draw ##DRAW_NAME##: ##DRAW_SIZE## convites.', result: '🎯 Draw Canadian Experience Class: 1,000 convites.' },
          { howTo: 'CRS mínimo: ##DRAW_CRS##. Parabéns aos selecionados! 🇨🇦', result: 'CRS mínimo: 475. Parabéns aos selecionados! 🇨🇦' },
          { howTo: 'Atualizado em ##CURRENT_DATE_US## (US) — ##CURRENT_DATE_BR## (BR). Dia ##DRAW_DAY_ORDINAL_US##. Siga para mais! 🔔', result: 'Atualizado em August 13, 2025 (US) — 13/08/2025 (BR). Dia 13th. Siga para mais! 🔔' },
          { howTo: '#ExpressEntry #CanadaImmigration #CRS #SkilledWorkers #ImmigrationNews', result: '#ExpressEntry #CanadaImmigration #CRS #SkilledWorkers #ImmigrationNews' }
        ];
        return wrap(<ExamplesTable rows={rows} />);
      }
      case FeatureTypeEnum.MANITOBA_CA_EOI_DRAWS: {
        const rows = [
          { howTo: '📍 Manitoba PNP — ##DRAW_NAME## em ##CURRENT_DATE_TEXT_EN##.', result: '📍 Manitoba PNP — Skilled Worker Overseas em October 30, 2026.' },
          { howTo: 'Convites emitidos para ##DRAW_NAME##. Boa sorte! 🍀', result: 'Convites emitidos para Skilled Worker Overseas. Boa sorte! 🍀' },
          { howTo: 'Atualização publicada em ##CURRENT_DATE_NUMERIC_DMY##, ##CURRENT_DATE_NUMERIC_MDY## e dia ##DRAW_DAY_ORDINAL_US##. 🇨🇦', result: 'Atualização publicada em 30/10/2026, 10/30/2026 e dia October 30th. 🇨🇦' },
          { howTo: 'Versões alternativas: ##CURRENT_DATE_ISO## e ##CURRENT_DATE_TEXT_PT_BR##.', result: 'Versões alternativas: 2026-10-30 e 30 de outubro de 2026.' },
          { howTo: '#ManitobaPNP #PNP #Immigration #Canada', result: '#ManitobaPNP #PNP #Immigration #Canada' }
        ];
        return wrap(<ExamplesTable rows={rows} />);
      }
      case FeatureTypeEnum.ONTARIO_CA_OINP_INVITATIONS: {
        const rows = [
          { howTo: '🧭 Ontario PNP — atualização em ##CURRENT_DATE_TEXT_EN_FORMAL##.', result: '🧭 Ontario PNP — atualização em October 30th, 2026.' },
          { howTo: 'Novos convites anunciados. Fique de olho! 👀', result: 'Novos convites anunciados. Fique de olho! 👀' },
          { howTo: 'Resumo rápido em ##CURRENT_DATE_NUMERIC_DMY## ou ##CURRENT_DATE_TEXT_PT_BR##. 🇨🇦', result: 'Resumo rápido em 30/10/2026 ou 30 de outubro de 2026. 🇨🇦' },
          { howTo: '#OntarioPNP #CanadaImmigration #PNP #Immigration', result: '#OntarioPNP #CanadaImmigration #PNP #Immigration' }
        ];
        return wrap(<ExamplesTable rows={rows} />);
      }
      case FeatureTypeEnum.ALBERTA_CA_AAIP_UPDATES: {
        const rows = [
          { howTo: '🏔️ Alberta Advantage — novidades em ##NEWS_UPDATE_TEXT_EN##.', result: '🏔️ Alberta Advantage — novidades em October 30, 2026.' },
          { howTo: 'Atualização: ##TITLE##. Siga-nos para mais! 🔔', result: 'Atualização: Alberta Advantage. Siga-nos para mais! 🔔' },
          { howTo: 'Dados oficiais em ##NEWS_UPDATE_NUMERIC_DMY## e ##NEWS_UPDATE_NUMERIC_MDY##. 🇨🇦', result: 'Dados oficiais em 30/10/2026 e 10/30/2026. 🇨🇦' },
          { howTo: 'Referência alternativa: ##NEWS_UPDATE_ISO## ou ##NEWS_UPDATE_TEXT_PT_BR##.', result: 'Referência alternativa: 2026-10-30 ou 30 de outubro de 2026.' },
          { howTo: '#AAIP #Alberta #ImmigrationNews #CanadaImmigration', result: '#AAIP #Alberta #ImmigrationNews #CanadaImmigration' }
        ];
        return wrap(<ExamplesTable rows={rows} />);
      }
      case FeatureTypeEnum.ALBERTA_CA_AAIP_DRAWS: {
        const rows = [
          { howTo: '🏔️ Novo draw AAIP em ##DRAW_DATE_NUMERIC_DMY##.', result: '🏔️ Novo draw AAIP em 20/08/2025.' },
          { howTo: 'Stream/Focus: ##WORKER_STREAM##.', result: 'Stream/Focus: Tourism and Hospitality.' },
          { howTo: 'Score mínimo: ##MINIMUM_SCORE## | Convites: ##INVITATIONS##.', result: 'Score mínimo: 302 | Convites: 150.' },
          { howTo: '#AAIP #Alberta #Draw #CanadaImmigration', result: '#AAIP #Alberta #Draw #CanadaImmigration' }
        ];
        return wrap(<ExamplesTable rows={rows} />);
      }
      case FeatureTypeEnum.CANADA_CA_IRCC_CHECK_PROCESSING_TIMES: {
        const rows = [
          { howTo: '⏳ Processing Times atualizado em ##CURRENT_DATE_TEXT_EN##.', result: '⏳ Processing Times atualizado em October 30, 2026.' },
          { howTo: 'Referência rápida: ##CURRENT_DATE_NUMERIC_DMY## | ##CURRENT_DATE_NUMERIC_MDY##.', result: 'Referência rápida: 30/10/2026 | 10/30/2026.' },
          { howTo: 'Versões alternativas: ##CURRENT_DATE_ISO## e ##CURRENT_DATE_TEXT_PT_BR##.', result: 'Versões alternativas: 2026-10-30 e 30 de outubro de 2026.' },
          { howTo: '#ProcessingTimes #IRCC #CanadaImmigration', result: '#ProcessingTimes #IRCC #CanadaImmigration' }
        ];
        return wrap(<ExamplesTable rows={rows} />);
      }
      case FeatureTypeEnum.COLUMBIA_CA_PNP:
      default:
        return null;
    }
  };

  const normalizeEmailValue = (email: string | undefined | null): string => (email || '').trim().toLowerCase();

  const getSectorLabel = (sector: number | null | undefined): string => {
    switch (Number(sector ?? 0)) {
      case SectorEnum.ADMINISTRADOR:
        return t('contact_manager_role_admin');
      case SectorEnum.ENGENHARIA_DE_SOFTWARE:
        return t('contact_manager_role_engineering');
      case SectorEnum.FINANCEIRO:
        return t('contact_manager_role_finance');
      case SectorEnum.GESTAO:
        return t('contact_manager_role_management');
      case SectorEnum.MARKETING:
        return t('contact_manager_role_marketing');
      case SectorEnum.CEO:
        return t('contact_manager_role_ceo');
      case SectorEnum.OUTRO:
        return t('contact_manager_role_other');
      default:
        return '';
    }
  };

  const formatSampleEmailOption = (item: AddressEmailOption): string => {
    const email = (item.email || '').trim();
    const name = (item.name || '').trim();
    const sectorLabel = getSectorLabel(item.sector);

    if (name && normalizeEmailValue(name) !== normalizeEmailValue(email)) {
      return sectorLabel ? `${name} (${email} | ${sectorLabel})` : `${name} (${email})`;
    }

    return sectorLabel ? `${email} (${sectorLabel})` : email;
  };

  const handleToggleSampleEmail = (email: string) => {
    const normalizedEmail = normalizeEmailValue(email);
    if (!normalizedEmail) return;

    setSampleSelectedEmails(prev => (
      prev.some(item => normalizeEmailValue(item) === normalizedEmail)
        ? prev.filter(item => normalizeEmailValue(item) !== normalizedEmail)
        : [...prev, email.trim()]
    ));
  };

  useEffect(() => {
    const loadFeatures = async () => {
      try {
        const response = await httpClient.get('/api/v1/portal/configurations/get-features-immigration-canada-global');
        const raw = response.data as unknown;
        let data: any | undefined;
        let errorMessages: string[] = [];

        if (raw && typeof raw === 'object') {
          const wrapped = raw as ApiSuccessResponse<any> & Partial<ApiErrorResponse>;
          if (wrapped.success === false && Array.isArray(wrapped.errors) && wrapped.errors.length) {
            errorMessages = wrapped.errors
              .map(e => (e && e.errorMessage) as string | undefined)
              .filter((msg): msg is string => !!msg);
          }
          data = (wrapped as ApiSuccessResponse<any>).data ?? (raw as any).data;
        }

        if (!data) {
          if (!errorMessages.length) {
            errorMessages = [t('features_error_load_resources')];
          }
          setFeaturesLoadErrors(errorMessages);
          setFeatures([]);
          return;
        }

        setFeaturesLoadErrors([]);

        const globalEmailOptions = Array.isArray(data?.addressesesEmailGeneralToNotify)
          ? data.addressesesEmailGeneralToNotify
          : (Array.isArray(data?.addressEmailGeneralToNotify) ? data.addressEmailGeneralToNotify : []);
        const customerEmailOptions = Array.isArray(data?.addressesesEmailCustomerToNotify) ? data.addressesesEmailCustomerToNotify : [];
        const combinedEmailOptions = [...globalEmailOptions, ...customerEmailOptions]
          .filter((item: any) => item && item.email)
          .reduce((acc: AddressEmailOption[], item: any) => {
            const email = String(item.email || '').trim();
            const normalizedEmail = normalizeEmailValue(email);
            if (!normalizedEmail) return acc;
            if (normalizedEmail === normalizeEmailValue(user?.email)) return acc;
            if (acc.some(existing => normalizeEmailValue(existing.email) === normalizedEmail)) return acc;

            acc.push({
              email,
              name: item.name ? String(item.name) : undefined,
              role: item.role ? String(item.role) : undefined,
              sector: item.sector === undefined || item.sector === null ? null : Number(item.sector)
            });
            return acc;
          }, []);
        setSampleEmailOptions(combinedEmailOptions);

        const featuresList: any[] = Array.isArray(data?.featuresImmigrationCanada) ? data.featuresImmigrationCanada : [];
        const getByType = (type: number) => featuresList.find((it) => it?.featureType === type) || {};
        setTelegramGroupUrl(data?.urlTelegramGroup || undefined);

        const toDailySample = (type: number) => ({
          dailySampleLimit: Number(getByType(type).dailySampleLimit ?? 0),
          dailySampleUsed: Number(getByType(type).dailySampleUsed ?? 0),
        });

        const toNotificationEmailType = (type: number): NotificationEmailTypeEnum => {
          const raw = (getByType(type) as any)?.notificationEmailType;
          const n = raw === undefined || raw === null ? undefined : Number(raw);
          return (n === NotificationEmailTypeEnum.USE_CUSTOM_ADDRESS || n === NotificationEmailTypeEnum.USE_GLOBAL_ADDRESS)
            ? (n as NotificationEmailTypeEnum)
            : NotificationEmailTypeEnum.USE_GLOBAL_ADDRESS;
        };

        const toEmailContacts = (list: any[] | undefined): ContactData['emails'] =>
          (list || []).map((item, idx) => ({
            id: `${item.email || 'email'}-${idx}-${Math.random().toString(36).slice(2,7)}`,
            email: item.email || '',
            name: item.name || '',
            role: (item.role ?? '0').toString(),
            sector: (item.sector ?? 0).toString()
          }));

        const toTemplateTags = (type: number): TemplateTag[] =>
          ((getByType(type).templateTags || []) as any[])
            .filter((item: any) => item && item.name)
            .map((item: any) => ({
              name: String(item.name),
              description: String(item.description || ''),
              exampleValue: String(item.exampleValue || '')
            }));

        const getDomainFromUrl = (url: string | undefined, fallback: string): string => {
          if (!url) return fallback;
          try {
            const parsed = new URL(url);
            return parsed.hostname.replace(/^www\./, '');
          } catch {
            return fallback;
          }
        };

            const mapped: Feature[] = [
              {
                id: '1',
                name: 'Express Entry - Draws',
                description: 'Monitoramento de novos draws do Express Entry',
                website: getDomainFromUrl((getByType(FeatureTypeEnum.CANADA_CA_IRCC_ROUNDS_OF_INVITATIONS).uRLReferenceWeb || '')?.toString().replace(/`/g, '').trim(), 'canada.ca'),
                enabled: !!getByType(FeatureTypeEnum.CANADA_CA_IRCC_ROUNDS_OF_INVITATIONS).enableByClient,
                enableByClientRaw: (getByType(FeatureTypeEnum.CANADA_CA_IRCC_ROUNDS_OF_INVITATIONS).enableByClient ?? null) as boolean | null,
                enableGlobally: !!getByType(FeatureTypeEnum.CANADA_CA_IRCC_ROUNDS_OF_INVITATIONS).enableGlobally,
                category: 'Federal',
                featureType: FeatureTypeEnum.CANADA_CA_IRCC_ROUNDS_OF_INVITATIONS,
                notificationEmailType: toNotificationEmailType(FeatureTypeEnum.CANADA_CA_IRCC_ROUNDS_OF_INVITATIONS),
                ...toDailySample(FeatureTypeEnum.CANADA_CA_IRCC_ROUNDS_OF_INVITATIONS),
                referenceUrl: (getByType(FeatureTypeEnum.CANADA_CA_IRCC_ROUNDS_OF_INVITATIONS).uRLReferenceWeb || '')?.toString().replace(/`/g, '').trim(),
                customContacts: {
                  emails: toEmailContacts(getByType(FeatureTypeEnum.CANADA_CA_IRCC_ROUNDS_OF_INVITATIONS).customEmailAddressNotify),
                  telegrams: [],
                  whatsapps: []
                },
                notifyTelegram: !!getByType(FeatureTypeEnum.CANADA_CA_IRCC_ROUNDS_OF_INVITATIONS).notifyTeletram,
                emBreve: !!getByType(FeatureTypeEnum.CANADA_CA_IRCC_ROUNDS_OF_INVITATIONS).asSoon,
                templateTags: toTemplateTags(FeatureTypeEnum.CANADA_CA_IRCC_ROUNDS_OF_INVITATIONS),
                socialMediaDescription: getByType(FeatureTypeEnum.CANADA_CA_IRCC_ROUNDS_OF_INVITATIONS).socialMediaPostDescription || '',
                templates: (getByType(FeatureTypeEnum.CANADA_CA_IRCC_ROUNDS_OF_INVITATIONS).templatesImages || []).map((img: any, idx: number) => ({
                  id: `express-${idx + 1}`,
                  name: `Template ${idx + 1}`,
                  preview: '',
                  thumbnail: '',
                  originalImage: img?.urlImageRaw || defaultTemplateImage,
                  parametrizedImage: img?.urlImageSample || defaultTemplateImage,
                  socialMediaLabel: getByType(FeatureTypeEnum.CANADA_CA_IRCC_ROUNDS_OF_INVITATIONS).socialMediaPostDescription || '',
                  guid: img?.guid
                }))
              },
              {
                id: '2',
                name: 'Ontario PNP - Draws',
                description: 'Atualizações do programa provincial de Ontario',
                website: getDomainFromUrl((getByType(FeatureTypeEnum.ONTARIO_CA_OINP_INVITATIONS).uRLReferenceWeb || '')?.toString().replace(/`/g, '').trim(), 'ontario.ca'),
                enabled: !!getByType(FeatureTypeEnum.ONTARIO_CA_OINP_INVITATIONS).enableByClient,
                enableByClientRaw: (getByType(FeatureTypeEnum.ONTARIO_CA_OINP_INVITATIONS).enableByClient ?? null) as boolean | null,
                enableGlobally: !!getByType(FeatureTypeEnum.ONTARIO_CA_OINP_INVITATIONS).enableGlobally,
                category: 'Provincial',
                featureType: FeatureTypeEnum.ONTARIO_CA_OINP_INVITATIONS,
                notificationEmailType: toNotificationEmailType(FeatureTypeEnum.ONTARIO_CA_OINP_INVITATIONS),
                ...toDailySample(FeatureTypeEnum.ONTARIO_CA_OINP_INVITATIONS),
                referenceUrl: (getByType(FeatureTypeEnum.ONTARIO_CA_OINP_INVITATIONS).uRLReferenceWeb || '')?.toString().replace(/`/g, '').trim(),
                customContacts: {
                  emails: toEmailContacts(getByType(FeatureTypeEnum.ONTARIO_CA_OINP_INVITATIONS).customEmailAddressNotify),
                  telegrams: [],
                  whatsapps: []
                },
                notifyTelegram: !!getByType(FeatureTypeEnum.ONTARIO_CA_OINP_INVITATIONS).notifyTeletram,
                emBreve: !!getByType(FeatureTypeEnum.ONTARIO_CA_OINP_INVITATIONS).asSoon,
                templateTags: toTemplateTags(FeatureTypeEnum.ONTARIO_CA_OINP_INVITATIONS),
                socialMediaDescription: getByType(FeatureTypeEnum.ONTARIO_CA_OINP_INVITATIONS).socialMediaPostDescription || '',
                templates: (getByType(FeatureTypeEnum.ONTARIO_CA_OINP_INVITATIONS).templatesImages || []).map((img: any, idx: number) => ({
                  id: `ontario-${idx + 1}`,
                  name: `Template ${idx + 1}`,
                  preview: '',
                  thumbnail: '',
                  originalImage: img?.urlImageRaw || defaultTemplateImage,
                  parametrizedImage: img?.urlImageSample || defaultTemplateImage,
                  socialMediaLabel: getByType(FeatureTypeEnum.ONTARIO_CA_OINP_INVITATIONS).socialMediaPostDescription || '',
                  guid: img?.guid
                }))
              },
              {
                id: '3',
                name: 'Manitoba PNP - Draws',
                description: 'Atualizações do programa provincial de Manitoba',
                website: getDomainFromUrl((getByType(FeatureTypeEnum.MANITOBA_CA_EOI_DRAWS).uRLReferenceWeb || '')?.toString().replace(/`/g, '').trim(), 'manitoba.ca'),
                enabled: !!getByType(FeatureTypeEnum.MANITOBA_CA_EOI_DRAWS).enableByClient,
                enableByClientRaw: (getByType(FeatureTypeEnum.MANITOBA_CA_EOI_DRAWS).enableByClient ?? null) as boolean | null,
                enableGlobally: !!getByType(FeatureTypeEnum.MANITOBA_CA_EOI_DRAWS).enableGlobally,
                category: 'Provincial',
                featureType: FeatureTypeEnum.MANITOBA_CA_EOI_DRAWS,
                notificationEmailType: toNotificationEmailType(FeatureTypeEnum.MANITOBA_CA_EOI_DRAWS),
                ...toDailySample(FeatureTypeEnum.MANITOBA_CA_EOI_DRAWS),
                referenceUrl: (getByType(FeatureTypeEnum.MANITOBA_CA_EOI_DRAWS).uRLReferenceWeb || '')?.toString().replace(/`/g, '').trim(),
                customContacts: {
                  emails: toEmailContacts(getByType(FeatureTypeEnum.MANITOBA_CA_EOI_DRAWS).customEmailAddressNotify),
                  telegrams: [],
                  whatsapps: []
                },
                notifyTelegram: !!getByType(FeatureTypeEnum.MANITOBA_CA_EOI_DRAWS).notifyTeletram,
                emBreve: !!getByType(FeatureTypeEnum.MANITOBA_CA_EOI_DRAWS).asSoon,
                templateTags: toTemplateTags(FeatureTypeEnum.MANITOBA_CA_EOI_DRAWS),
                socialMediaDescription: getByType(FeatureTypeEnum.MANITOBA_CA_EOI_DRAWS).socialMediaPostDescription || '',
                templates: (getByType(FeatureTypeEnum.MANITOBA_CA_EOI_DRAWS).templatesImages || []).map((img: any, idx: number) => ({
                  id: `manitoba-${idx + 1}`,
                  name: `Template ${idx + 1}`,
                  preview: '',
                  thumbnail: '',
                  originalImage: img?.urlImageRaw || defaultTemplateImage,
                  parametrizedImage: img?.urlImageSample || defaultTemplateImage,
                  socialMediaLabel: getByType(FeatureTypeEnum.MANITOBA_CA_EOI_DRAWS).socialMediaPostDescription || '',
                  guid: img?.guid
                }))
              },
              {
                id: '4',
                name: 'Processing Times',
                description: 'Tempos de processamento para diferentes tipos de visto',
                website: getDomainFromUrl((getByType(FeatureTypeEnum.CANADA_CA_IRCC_CHECK_PROCESSING_TIMES).uRLReferenceWeb || '')?.toString().replace(/`/g, '').trim(), 'canada.ca'),
                enabled: !!getByType(FeatureTypeEnum.CANADA_CA_IRCC_CHECK_PROCESSING_TIMES).enableByClient,
                enableByClientRaw: (getByType(FeatureTypeEnum.CANADA_CA_IRCC_CHECK_PROCESSING_TIMES).enableByClient ?? null) as boolean | null,
                enableGlobally: !!getByType(FeatureTypeEnum.CANADA_CA_IRCC_CHECK_PROCESSING_TIMES).enableGlobally,
                category: 'Federal',
                featureType: FeatureTypeEnum.CANADA_CA_IRCC_CHECK_PROCESSING_TIMES,
                notificationEmailType: toNotificationEmailType(FeatureTypeEnum.CANADA_CA_IRCC_CHECK_PROCESSING_TIMES),
                ...toDailySample(FeatureTypeEnum.CANADA_CA_IRCC_CHECK_PROCESSING_TIMES),
                referenceUrl: (getByType(FeatureTypeEnum.CANADA_CA_IRCC_CHECK_PROCESSING_TIMES).uRLReferenceWeb || '')?.toString().replace(/`/g, '').trim(),
                customContacts: {
                  emails: toEmailContacts(getByType(FeatureTypeEnum.CANADA_CA_IRCC_CHECK_PROCESSING_TIMES).customEmailAddressNotify),
                  telegrams: [],
                  whatsapps: []
                },
                notifyTelegram: !!getByType(FeatureTypeEnum.CANADA_CA_IRCC_CHECK_PROCESSING_TIMES).notifyTeletram,
                emBreve: !!getByType(FeatureTypeEnum.CANADA_CA_IRCC_CHECK_PROCESSING_TIMES).asSoon,
                templateTags: toTemplateTags(FeatureTypeEnum.CANADA_CA_IRCC_CHECK_PROCESSING_TIMES),
                socialMediaDescription: getByType(FeatureTypeEnum.CANADA_CA_IRCC_CHECK_PROCESSING_TIMES).socialMediaPostDescription || '',
                templates: (getByType(FeatureTypeEnum.CANADA_CA_IRCC_CHECK_PROCESSING_TIMES).templatesImages || []).map((img: any, idx: number) => ({
                  id: `processing-${idx + 1}`,
                  name: `Template ${idx + 1}`,
                  preview: '',
                  thumbnail: '',
                  originalImage: img?.urlImageRaw || defaultTemplateImage,
                  parametrizedImage: img?.urlImageSample || defaultTemplateImage,
                  socialMediaLabel: getByType(FeatureTypeEnum.CANADA_CA_IRCC_CHECK_PROCESSING_TIMES).socialMediaPostDescription || '',
                  guid: img?.guid
                }))
              },
              {
                id: '5',
                name: 'British Columbia PNP - Draws',
                description: 'Atualizações do programa provincial de British Columbia',
                website: getDomainFromUrl((getByType(FeatureTypeEnum.COLUMBIA_CA_PNP).uRLReferenceWeb || '')?.toString().replace(/`/g, '').trim(), 'welcomebc.ca'),
                enabled: !!getByType(FeatureTypeEnum.COLUMBIA_CA_PNP).enableByClient,
                enableByClientRaw: (getByType(FeatureTypeEnum.COLUMBIA_CA_PNP).enableByClient ?? null) as boolean | null,
                enableGlobally: !!getByType(FeatureTypeEnum.COLUMBIA_CA_PNP).enableGlobally,
                category: 'Provincial',
                featureType: FeatureTypeEnum.COLUMBIA_CA_PNP,
                notificationEmailType: toNotificationEmailType(FeatureTypeEnum.COLUMBIA_CA_PNP),
                ...toDailySample(FeatureTypeEnum.COLUMBIA_CA_PNP),
                referenceUrl: (getByType(FeatureTypeEnum.COLUMBIA_CA_PNP).uRLReferenceWeb || '')?.toString().replace(/`/g, '').trim(),
                customContacts: {
                  emails: toEmailContacts(getByType(FeatureTypeEnum.COLUMBIA_CA_PNP).customEmailAddressNotify),
                  telegrams: [],
                  whatsapps: []
                },
                notifyTelegram: !!getByType(FeatureTypeEnum.COLUMBIA_CA_PNP).notifyTeletram,
                emBreve: !!getByType(FeatureTypeEnum.COLUMBIA_CA_PNP).asSoon,
                templateTags: toTemplateTags(FeatureTypeEnum.COLUMBIA_CA_PNP),
                socialMediaDescription: getByType(FeatureTypeEnum.COLUMBIA_CA_PNP).socialMediaPostDescription || '',
                templates: (getByType(FeatureTypeEnum.COLUMBIA_CA_PNP).templatesImages || []).map((img: any, idx: number) => ({
                  id: `bc-${idx + 1}`,
                  name: `Template ${idx + 1}`,
                  preview: '',
                  thumbnail: '',
                  originalImage: img?.urlImageRaw || defaultTemplateImage,
                  parametrizedImage: img?.urlImageSample || defaultTemplateImage,
                  socialMediaLabel: getByType(FeatureTypeEnum.COLUMBIA_CA_PNP).socialMediaPostDescription || '',
                  guid: img?.guid
                }))
              },
              {
                id: '7',
                name: 'Alberta AAIP - Draws',
                description: 'Monitoramento de novos draws do AAIP',
                website: getDomainFromUrl((getByType(FeatureTypeEnum.ALBERTA_CA_AAIP_DRAWS).uRLReferenceWeb || '')?.toString().replace(/`/g, '').trim(), 'alberta.ca'),
                enabled: !!getByType(FeatureTypeEnum.ALBERTA_CA_AAIP_DRAWS).enableByClient,
                enableByClientRaw: (getByType(FeatureTypeEnum.ALBERTA_CA_AAIP_DRAWS).enableByClient ?? null) as boolean | null,
                enableGlobally: !!getByType(FeatureTypeEnum.ALBERTA_CA_AAIP_DRAWS).enableGlobally,
                category: 'Provincial',
                featureType: FeatureTypeEnum.ALBERTA_CA_AAIP_DRAWS,
                notificationEmailType: toNotificationEmailType(FeatureTypeEnum.ALBERTA_CA_AAIP_DRAWS),
                ...toDailySample(FeatureTypeEnum.ALBERTA_CA_AAIP_DRAWS),
                referenceUrl: (getByType(FeatureTypeEnum.ALBERTA_CA_AAIP_DRAWS).uRLReferenceWeb || '')?.toString().replace(/`/g, '').trim(),
                customContacts: {
                  emails: toEmailContacts(getByType(FeatureTypeEnum.ALBERTA_CA_AAIP_DRAWS).customEmailAddressNotify),
                  telegrams: [],
                  whatsapps: []
                },
                notifyTelegram: !!getByType(FeatureTypeEnum.ALBERTA_CA_AAIP_DRAWS).notifyTeletram,
                emBreve: !!getByType(FeatureTypeEnum.ALBERTA_CA_AAIP_DRAWS).asSoon,
                templateTags: toTemplateTags(FeatureTypeEnum.ALBERTA_CA_AAIP_DRAWS),
                socialMediaDescription: getByType(FeatureTypeEnum.ALBERTA_CA_AAIP_DRAWS).socialMediaPostDescription || '',
                templates: (getByType(FeatureTypeEnum.ALBERTA_CA_AAIP_DRAWS).templatesImages || []).map((img: any, idx: number) => ({
                  id: `alberta-draws-${idx + 1}`,
                  name: `Template ${idx + 1}`,
                  preview: '',
                  thumbnail: '',
                  originalImage: img?.urlImageRaw || defaultTemplateImage,
                  parametrizedImage: img?.urlImageSample || defaultTemplateImage,
                  socialMediaLabel: getByType(FeatureTypeEnum.ALBERTA_CA_AAIP_DRAWS).socialMediaPostDescription || '',
                  guid: img?.guid
                }))
              },
              {
                id: '6',
                name: 'Alberta AAIP - Updates',
                description: 'Tempos de processamento e atualizações do AAIP',
                website: getDomainFromUrl((getByType(FeatureTypeEnum.ALBERTA_CA_AAIP_UPDATES).uRLReferenceWeb || '')?.toString().replace(/`/g, '').trim(), 'alberta.ca'),
                enabled: !!getByType(FeatureTypeEnum.ALBERTA_CA_AAIP_UPDATES).enableByClient,
                enableByClientRaw: (getByType(FeatureTypeEnum.ALBERTA_CA_AAIP_UPDATES).enableByClient ?? null) as boolean | null,
                enableGlobally: !!getByType(FeatureTypeEnum.ALBERTA_CA_AAIP_UPDATES).enableGlobally,
                category: 'Provincial',
                featureType: FeatureTypeEnum.ALBERTA_CA_AAIP_UPDATES,
                notificationEmailType: toNotificationEmailType(FeatureTypeEnum.ALBERTA_CA_AAIP_UPDATES),
                ...toDailySample(FeatureTypeEnum.ALBERTA_CA_AAIP_UPDATES),
                referenceUrl: (getByType(FeatureTypeEnum.ALBERTA_CA_AAIP_UPDATES).uRLReferenceWeb || '')?.toString().replace(/`/g, '').trim(),
                customContacts: {
                  emails: toEmailContacts(getByType(FeatureTypeEnum.ALBERTA_CA_AAIP_UPDATES).customEmailAddressNotify),
                  telegrams: [],
                  whatsapps: []
                },
                notifyTelegram: !!getByType(FeatureTypeEnum.ALBERTA_CA_AAIP_UPDATES).notifyTeletram,
                emBreve: !!getByType(FeatureTypeEnum.ALBERTA_CA_AAIP_UPDATES).asSoon,
                templateTags: toTemplateTags(FeatureTypeEnum.ALBERTA_CA_AAIP_UPDATES),
                socialMediaDescription: getByType(FeatureTypeEnum.ALBERTA_CA_AAIP_UPDATES).socialMediaPostDescription || '',
                templates: (getByType(FeatureTypeEnum.ALBERTA_CA_AAIP_UPDATES).templatesImages || []).map((img: any, idx: number) => ({
                  id: `alberta-${idx + 1}`,
                  name: `Template ${idx + 1}`,
                  preview: '',
                  thumbnail: '',
                  originalImage: img?.urlImageRaw || defaultTemplateImage,
                  parametrizedImage: img?.urlImageSample || defaultTemplateImage,
                  socialMediaLabel: getByType(FeatureTypeEnum.ALBERTA_CA_AAIP_UPDATES).socialMediaPostDescription || '',
                  guid: img?.guid
                }))
              }
            ];

        setFeatures((prev) => {
          // Preserve existing static template data if present by merging on id
          const byId = new Map(prev.map(p => [p.id, p]));
          return mapped.map(m => {
            const existing = byId.get(m.id);
            // Prefer API-provided templates if present; otherwise keep existing
            const templates = (m.templates && m.templates.length) ? m.templates : existing?.templates;
            return existing ? { ...existing, ...m, templates } : { ...m, templates };
          });
        });
      } catch (error: any) {
        console.error(t('features_error_load_resources'), error);
        const respData = error?.response?.data as Partial<ApiErrorResponse> | undefined;
        const collected: string[] = [];
        if (respData && Array.isArray(respData.errors)) {
          for (const item of respData.errors) {
            const msg = (item && item.errorMessage) as string | undefined;
            if (msg) collected.push(msg);
          }
        }
        if (!collected.length && respData && typeof (respData as any).message === 'string') {
          collected.push((respData as any).message);
        }
        setFeaturesLoadErrors(collected.length ? collected : [t('features_error_load_resources')]);
        setFeatures([]);
      }
    };
    loadFeatures();
  }, [t, user?.email]);

  const handleToggleFeature = async (id: string) => {
    const feature = features.find(f => f.id === id);
    if (!feature) return;

    const newActive = !feature.enabled;
    try {
      const payload = { featureType: feature.featureType, active: newActive };
      const response = await httpClient.post('/api/v1/portal/configurations/update-feature-activation-status', payload);

      // Interpret response: wrapped success with data:boolean or possible error schema
      const raw = response.data as unknown;
      let ok = false;
      if (raw && typeof raw === 'object') {
        const wrapped = raw as ApiSuccessResponse<boolean> & Partial<ApiErrorResponse>;
        if (typeof wrapped.data === 'boolean') {
          ok = wrapped.data;
        }
        if (!ok && Array.isArray(wrapped.errors)) {
          const msg = wrapped.errors
            .map(e => `${e.propertyName}: ${e.errorMessage}`)
            .join('; ');
          setAlertMessage(msg || t('features_alert_error_update_status'));
          setAlertOpen(true);
          return;
        }
      } else if (typeof raw === 'boolean') {
        ok = raw;
      }

      if (!ok) {
        setAlertMessage(t('features_alert_error_update_status'));
        setAlertOpen(true);
        return;
      }

      // Success: update local state and notify
      setFeatures(features.map(f => f.id === id ? { ...f, enabled: newActive, enableByClientRaw: newActive } : f));
      setAlertMessage(t('features_alert_success_toggle', { name: feature.name, status: newActive ? t('features_status_enabled') : t('features_status_disabled') }));
      setAlertOpen(true);
    } catch (error) {
      console.error('Erro ao atualizar status da feature:', error);
      setAlertMessage(t('features_alert_error_update_status'));
      setAlertOpen(true);
    }
  };

  const handleDispatchSampleEmail = (feature: Feature) => {
    setSampleConfirmFeature(feature);
    setSampleSelectedEmails([]);
    setSampleConfirmOpen(true);
  };

  const handleCloseSampleConfirmDialog = () => {
    if (sampleConfirmLoading) return;
    setSampleConfirmOpen(false);
    setSampleConfirmFeature(null);
    setSampleSelectedEmails([]);
  };

  const handleConfirmDispatchSampleEmail = async () => {
    if (!sampleConfirmFeature) return;

    const feature = sampleConfirmFeature;
    const limit = Number(feature.dailySampleLimit ?? 0);
    const used = Number(feature.dailySampleUsed ?? 0);
    if (limit > 0 && used >= limit) {
      setAlertSeverity('warning');
      setAlertMessage(t('features_alert_warning_sample_limit'));
      setAlertOpen(true);
      setSampleConfirmOpen(false);
      return;
    }

    setSampleConfirmLoading(true);
    setDispatchingSampleById(prev => ({ ...prev, [feature.id]: true }));
    try {
      const response = await httpClient.post('/api/v1/portal/notifications/dispatch-test-feature', {
        featureType: feature.featureType,
        emailAddressesToNotify: sampleSelectedEmails
      });
      const raw = response.data as unknown;

      type DispatchResponse = { dispatched?: boolean; dailySampleUsed?: number };
      let ok = false;
      let dailySampleUsedFromApi: number | null = null;
      let errorMessages: string[] = [];

      if (raw && typeof raw === 'object') {
        const wrapped = raw as ApiSuccessResponse<DispatchResponse | boolean> & Partial<ApiErrorResponse>;

        if (typeof wrapped.data === 'boolean') {
          ok = wrapped.data;
        } else if (wrapped.data && typeof wrapped.data === 'object') {
          ok = (wrapped.data as DispatchResponse).dispatched === true;
          const parsedUsed = Number((wrapped.data as DispatchResponse).dailySampleUsed);
          dailySampleUsedFromApi = Number.isFinite(parsedUsed) ? parsedUsed : null;
        }

        if (!ok && Array.isArray(wrapped.errors) && wrapped.errors.length) {
          errorMessages = wrapped.errors
            .map(e => (e && e.errorMessage) as string | undefined)
            .filter((msg): msg is string => !!msg);
        }
      } else if (typeof raw === 'boolean') {
        ok = raw;
      }

      if (!ok) {
        setAlertSeverity('error');
        setAlertMessage(errorMessages.join('; ') || t('features_alert_error_sample_email'));
        setAlertOpen(true);
        return;
      }

      setFeatures(prev =>
        prev.map(f => {
          if (f.id !== feature.id) return f;
          const nextUsed = dailySampleUsedFromApi !== null ? dailySampleUsedFromApi : (Number(f.dailySampleUsed ?? 0) + 1);
          return { ...f, dailySampleUsed: nextUsed };
        })
      );
      setAlertSeverity('success');
      setAlertMessage(t('features_alert_success_sample_email'));
      setAlertOpen(true);
    } catch (error: any) {
      const status = error?.response?.status as number | undefined;
      const respData = error?.response?.data as Partial<ApiErrorResponse> | undefined;
      const collected: string[] = [];
      if (respData && Array.isArray(respData.errors)) {
        for (const item of respData.errors) {
          const msg = (item && item.errorMessage) as string | undefined;
          if (msg) collected.push(msg);
        }
      }

      setAlertSeverity(status === 429 ? 'warning' : 'error');
      setAlertMessage(collected.join('; ') || t('features_alert_error_sample_email'));
      setAlertOpen(true);
    } finally {
      setDispatchingSampleById(prev => ({ ...prev, [feature.id]: false }));
      setSampleConfirmLoading(false);
      setSampleConfirmOpen(false);
      setSampleConfirmFeature(null);
      setSampleSelectedEmails([]);
    }
  };

  const handleOpenNotificationSettings = (feature: Feature) => {
    setSelectedFeature({
      ...feature,
      customContacts: feature.customContacts || {
        emails: [],
        telegrams: [],
        whatsapps: []
      }
    });
    setNotificationDialogOpen(true);
  };

  const handleSaveNotificationSettings = async () => {
    if (!selectedFeature) return;
    try {
      // Map emails from ContactManager to API schema
      const customEmails = (selectedFeature.customContacts?.emails || []).map(e => ({
        name: e.name || '',
        email: e.email || '',
        role: e.role || '0',
        sector: Number(e.sector || 0),
      }));

      const payload = {
        notificationEmailType: selectedFeature.notificationEmailType,
        addressEmailGeneralToNotify: selectedFeature.notificationEmailType === NotificationEmailTypeEnum.USE_GLOBAL_ADDRESS ? [] : [],
        addressEmailCustomToNotify: selectedFeature.notificationEmailType === NotificationEmailTypeEnum.USE_CUSTOM_ADDRESS ? customEmails : [],
        telegramNotify: !!selectedFeature.notifyTelegram,
        featureType: selectedFeature.featureType,
      };

      const response = await httpClient.post('/api/v1/portal/configurations/save-or-update-features', payload);

      // Handle API returning a boolean or wrapped response with data:boolean
      let ok: boolean = false;
      const raw = response.data as unknown;
      if (typeof raw === 'boolean') {
        ok = raw;
      } else if (raw && typeof raw === 'object') {
        const wrapped = raw as ApiSuccessResponse<boolean> & Partial<ApiErrorResponse>;
        if (typeof (wrapped as ApiSuccessResponse<boolean>).data === 'boolean') {
          ok = (wrapped as ApiSuccessResponse<boolean>).data;
        }
        // If server returns error schema, surface messages
        if (!ok && Array.isArray((wrapped as ApiErrorResponse).errors)) {
          const msg = (wrapped as ApiErrorResponse).errors
            .map(e => `${e.propertyName}: ${e.errorMessage}`)
            .join('; ');
          setAlertMessage(msg || t('features_alert_error_save_notification'));
          setAlertOpen(true);
          return;
        }
      }

      if (!ok) {
        setAlertMessage(t('features_alert_error_save_notification'));
        setAlertOpen(true);
        return;
      }

      setFeatures(features.map(feature =>
        feature.id === selectedFeature.id ? selectedFeature : feature
      ));
      setNotificationDialogOpen(false);
      setAlertMessage(t('features_alert_success_save_notification'));
      setAlertOpen(true);
    } catch (error) {
      console.error('Erro ao salvar configurações de notificação:', error);
      setAlertMessage(t('features_alert_error_save_notification'));
      setAlertOpen(true);
    }
  };

  const handleNotificationTypeChange = (type: NotificationEmailTypeEnum) => {
    if (selectedFeature) {
      setSelectedFeature({
        ...selectedFeature,
        notificationEmailType: type
      });
    }
  };

  const handleContactsChange = (contacts: ContactData) => {
    if (selectedFeature) {
      setSelectedFeature({
        ...selectedFeature,
        customContacts: contacts
      });
    }
  };

  const handleOpenTemplateDialog = (feature: Feature) => {
    // Always open the dialog, even if templates are empty
    setSelectedFeature(feature);

    // Reset image loading states whenever opening the dialog
    setOriginalImageLoading(true);
    setParamImageLoading(true);

    const hasTemplates = feature.templates && feature.templates.length > 0;

    if (hasTemplates) {
      const firstTemplate: Template = feature.templates![0];
      setSelectedTemplate(firstTemplate);
      setCurrentTemplateIndex(0);
      setSocialLabelText(firstTemplate.socialMediaLabel || '');
    } else {
      // No templates available: present CTA in Templates tab and keep social label from feature
      setSelectedTemplate(null);
      setCurrentTemplateIndex(0);
      setSocialLabelText(feature.socialMediaDescription || '');
    }

    setTemplateDialogOpen(true);
    // Default to the first tab (Legenda e instruções) after inversion
    setTemplateDialogTab(0);
  };

  const handleNextTemplate = () => {
    if (selectedFeature?.templates) {
      // Reset loaders for next image
      setOriginalImageLoading(true);
      setParamImageLoading(true);
      const nextIndex = (currentTemplateIndex + 1) % selectedFeature.templates.length;
      setCurrentTemplateIndex(nextIndex);
      setSelectedTemplate(selectedFeature.templates[nextIndex]);
      setSocialLabelText(selectedFeature.templates[nextIndex].socialMediaLabel);
    }
  };

  const handlePrevTemplate = () => {
    if (selectedFeature?.templates) {
      // Reset loaders for previous image
      setOriginalImageLoading(true);
      setParamImageLoading(true);
      const prevIndex = currentTemplateIndex === 0 
        ? selectedFeature.templates.length - 1 
        : currentTemplateIndex - 1;
      setCurrentTemplateIndex(prevIndex);
      setSelectedTemplate(selectedFeature.templates[prevIndex]);
      setSocialLabelText(selectedFeature.templates[prevIndex].socialMediaLabel);
    }
  };

  const handleSaveSocialLabel = async () => {
    try {
      if (!selectedFeature) return;

      const payload = {
        featureType: selectedFeature.featureType,
        socialMediaDescription: socialLabelText?.trim() ?? ''
      };

      const response = await httpClient.post('/api/v1/portal/configurations/save-or-update-social-media-description', payload);

      // Interpret possible response shapes (boolean or wrapped)
      const raw = response.data as unknown;
      let ok = false;
      let message: string | undefined;

      if (typeof raw === 'boolean') {
        ok = raw;
      } else if (raw && typeof raw === 'object') {
        const wrapped = raw as ApiSuccessResponse<boolean> & Partial<ApiErrorResponse>;
        if (typeof wrapped.data === 'boolean') {
          ok = wrapped.data;
        }
        message = wrapped.message;
        if (!ok && Array.isArray(wrapped.errors)) {
          const msg = wrapped.errors
            .map(e => e.errorMessage || `${e.propertyName}: ${e.errorMessage}`)
            .join('\n');
          setAlertMessage(msg || t('features_alert_error_save_social_label'));
          setAlertSeverity('error');
          setAlertOpen(true);
          return;
        }
      }

      if (ok) {
        // Update local state: selectedTemplate and features list
        if (selectedFeature?.templates && selectedFeature.templates.length > 0) {
          const updatedTemplates = selectedFeature.templates.map((t, idx) =>
            idx === currentTemplateIndex ? { ...t, socialMediaLabel: socialLabelText } : t
          );
          const updatedFeature = { ...selectedFeature, templates: updatedTemplates };
          setSelectedFeature(updatedFeature);
          setSelectedTemplate(updatedTemplates[currentTemplateIndex]);
          setFeatures(features.map(f => f.id === updatedFeature.id ? updatedFeature : f));
        } else {
          // No templates: persist the fallback description on the feature
          const updatedFeature = { ...selectedFeature, socialMediaDescription: socialLabelText };
          setSelectedFeature(updatedFeature);
          setFeatures(features.map(f => f.id === updatedFeature.id ? updatedFeature : f));
        }
        setAlertMessage(message || t('features_alert_success_save_social_label'));
        setAlertSeverity('success');
        setAlertOpen(true);
      } else {
        setAlertMessage(message || t('features_alert_error_save_social_label'));
        setAlertSeverity('error');
        setAlertOpen(true);
      }
    } catch (error) {
      const respData = (error as any)?.response?.data;
      const collected: string[] = [];
      if (respData) {
        if (Array.isArray(respData.errors)) {
          for (const item of respData.errors) {
            const msg = (item && (item.errorMessage || item.message)) as string | undefined;
            if (msg) collected.push(msg);
          }
        }
        if (respData.message && typeof respData.message === 'string') {
          collected.push(respData.message);
        }
      }
      setAlertMessage(collected.length ? collected.join('\n') : t('features_error_save_social_label'));
      setAlertSeverity('error');
      setAlertOpen(true);
      console.error('Error updating social media label:', error);
    }
  };

  const [templateChangeFromTemplateDialog, setTemplateChangeFromTemplateDialog] = useState<boolean>(false);
  const handleNotifyTemplateChange = () => {
    // abertura direta pelo botão "Selecionar mudança de template": desbloquear seleção
    setTemplateChangeFromTemplateDialog(false);
    setTemplateChangeModalOpen(true);
  };
  const handleTemplateChangeSubmit = async (selectedFeatures: string[], description: string, originalImage: File | null, finalImage: File | null) => {
    try {
      // Map selected feature ids to their numeric featureType codes
      const featureTypes: number[] = selectedFeatures
        .map(id => features.find(f => f.id === id)?.featureType)
        .filter((v): v is number => typeof v === 'number');

      // If no valid featureTypes, surface an error
      if (!featureTypes.length) {
        setAlertMessage(t('features_alert_select_valid_feature'));
        setAlertSeverity('error');
        setAlertOpen(true);
        return;
      }

      // Monta multipart/form-data conforme RequestTemplateChangeDto
      const formData = new FormData();
      featureTypes.forEach(ft => formData.append('featureTypes', String(ft)));
      formData.append('description', (description?.trim() ?? '').slice(0, 300));
      if (originalImage) {
        formData.append('imageTemplateRaw', originalImage, originalImage.name);
      }
      if (finalImage) {
        formData.append('imageTemplateSample', finalImage, finalImage.name);
      }

      const response = await httpClient.post('/api/v1/portal/configurations/request-templates-changes', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // Interpret possible response shapes
      const raw = response.data as unknown;
      let ok = false;
      let message: string | undefined;

      if (typeof raw === 'boolean') {
        ok = raw;
      } else if (raw && typeof raw === 'object') {
        const wrapped = raw as ApiSuccessResponse<boolean> & Partial<ApiErrorResponse>;
        if (typeof wrapped.data === 'boolean') {
          ok = wrapped.data;
        }
        message = wrapped.message;
        if (!ok && Array.isArray(wrapped.errors)) {
          const msg = wrapped.errors.map(e => e.errorMessage || `${e.propertyName}: ${e.errorMessage}`).join('\n');
          setAlertMessage(msg || t('features_alert_error_request_template_changes'));
          setAlertSeverity('error');
          setAlertOpen(true);
          return;
        }
      }

      if (ok) {
        setAlertMessage(message || t('features_alert_success_request_template_changes'));
        setAlertSeverity('success');
        setAlertDuration(5000);
        setAlertOpen(true);
        // Fechar a modal "Solicitação de cadastro de Template" somente em sucesso
        setTemplateChangeModalOpen(false);
      } else {
        setAlertMessage(message || t('features_alert_error_request_template_changes'));
        setAlertSeverity('error');
        setAlertOpen(true);
      }
    } catch (error) {
      const respData = (error as any)?.response?.data;
      const collected: string[] = [];
      if (respData) {
        if (Array.isArray(respData.errors)) {
          for (const item of respData.errors) {
            const msg = (item && (item.errorMessage || item.message)) as string | undefined;
            if (msg) collected.push(msg);
          }
        }
        if (respData.message && typeof respData.message === 'string') {
          collected.push(respData.message);
        }
      }
      setAlertMessage(collected.length ? collected.join('\n') : t('features_error_request_template_changes'));
      setAlertSeverity('error');
      setAlertOpen(true);
      console.error('Error requesting template changes:', error);
    }
  };

  // Group features by category
  const groupedFeatures: Record<string, Feature[]> = features.reduce((acc, feature) => {
    if (!acc[feature.category]) {
      acc[feature.category] = [];
    }
    acc[feature.category].push(feature);
    return acc;
  }, {} as Record<string, Feature[]>);

  // Sort 'Provincial' alphabetically by name
  if (groupedFeatures['Provincial']) {
    groupedFeatures['Provincial'].sort((a, b) => a.name.localeCompare(b.name));
  }

  // Help dialog state for "Como Funciona?"
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const handleOpenHelpDialog = () => setHelpDialogOpen(true);
  const userEmail = (user?.email || '').trim();
  const sampleLimitReached = !!sampleConfirmFeature
    && Number(sampleConfirmFeature.dailySampleLimit ?? 0) > 0
    && Number(sampleConfirmFeature.dailySampleUsed ?? 0) >= Number(sampleConfirmFeature.dailySampleLimit ?? 0);

  return (
    <MainLayout>
      <Box sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h4" component="h1" gutterBottom color="primary" sx={{ fontWeight: 'bold', mb: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
              <SettingsIcon />
            {t('features_title_main')}
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleOpenHelpDialog}
          >
            {t('features_btn_how_it_works')}
          </Button>
        </Box>
        {/* Warning alert when no features are enabled by client */}
        {features.length > 0 && !features.some((f) => f.enabled) && (
          <MuiAlert severity="warning" sx={{ mb: 2 }}>
            {t('features_alert_none_enabled')}
          </MuiAlert>
        )}
        <ApiConnectionAlert />

        <Typography variant="body1" paragraph sx={{ mb: 4 }}>
          {t('features_description_main')}
        </Typography>

        {featuresLoadErrors.length > 0 && (
          <MuiAlert severity="error" sx={{ mb: 3 }}>
            {featuresLoadErrors.map((msg, idx) => (
              <Box key={idx}>{msg}</Box>
            ))}
          </MuiAlert>
        )}

        {/* Help Dialog */}
        <Dialog
          open={helpDialogOpen}
          onClose={() => setHelpDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: dialogPaperSx }}
        >
          <DialogTitle>
            {t('features_help_title')}
          </DialogTitle>
          <DialogContent sx={dialogContentSx}>
            <Typography variant="body1" paragraph>
              {t('features_help_p1')}
            </Typography>
            <Typography variant="body1" paragraph>
              {t('features_help_p2')}
            </Typography>
            <Typography variant="body1" paragraph>
              {t('features_help_p3')}
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
              {t('features_help_howto_title')}
            </Typography>
            <Typography variant="body1" component="ol" sx={{ pl: 2 }}>
              <li>{t('features_help_howto_item1')}</li>
              <li>{t('features_help_howto_item2')}</li>
              <li>{t('features_help_howto_item3')}</li>
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHelpDialogOpen(false)}>{t('features_help_btn_close')}</Button>
          </DialogActions>
        </Dialog>

        {Object.entries(groupedFeatures).map(([category, categoryFeatures]) => (
          <Paper key={category} sx={{ mb: 4, p: 3, borderRadius: 2 }}>
            <Typography variant="h5" gutterBottom color="primary" sx={{ mb: 3 }}>
              {category === 'Federal' ? t('features_category_federal') : t('features_category_provincial')}
            </Typography>

            <Grid container spacing={3}>
              {categoryFeatures.map((feature) => (
                <Grid
                  item
                  component={'div' as React.ElementType}
                  key={feature.id}
                  xs={12}
                  sm={6}
                  md={4}
                >
                  <Card
                    sx={{
                      height: '100%',
                      borderLeft: feature.enabled ? '4px solid #322954' : '4px solid #ccc',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: 3,
                      },
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 2,
                        }}
                      >
                        <Typography variant="h6">{feature.name}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {feature.emBreve && (
                            <Chip
                              label={t('features_chip_coming_soon')}
                              size="small"
                              color="warning"
                              sx={{ fontSize: '0.75rem' }}
                            />
                          )}
                          {!feature.emBreve && (
                            <>
                              {feature.enableByClientRaw === null && (
                                <Box
                                  sx={{
                                    position: 'relative',
                                    px: 1.5,
                                    py: 0.5,
                                    mr: 0.5,
                                    bgcolor: '#ff9800',
                                    color: '#fff',
                                    borderRadius: 1,
                                    fontSize: '0.65rem',
                                    fontWeight: 'bold',
                                    '&::after': {
                                      content: '""',
                                      position: 'absolute',
                                      right: -6,
                                      top: '50%',
                                      transform: 'translateY(-50%)',
                                      borderWidth: '6px 0 6px 6px',
                                      borderStyle: 'solid',
                                      borderColor: 'transparent transparent transparent #ff9800',
                                    },
                                  }}
                                >
                                  Novo
                                </Box>
                              )}
                              <Switch
                                checked={feature.enabled}
                                onChange={() => handleToggleFeature(feature.id)}
                                color="primary"
                              />
                            </>
                          )}
                        </Box>
                      </Box>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {feature.description}
                      </Typography>

                      <Divider sx={{ my: 1 }} />

                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mt: 2,
                          mb: 2
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {t('features_label_source')}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {feature.referenceUrl ? (
                            <Chip
                              label={feature.website}
                              size="small"
                              component="a"
                              href={feature.referenceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              clickable
                              sx={{
                                bgcolor: feature.enabled
                                  ? 'rgba(50, 41, 84, 0.1)'
                                  : 'rgba(0, 0, 0, 0.08)',
                                color: feature.enabled ? 'primary.main' : 'text.secondary',
                                textDecoration: 'none'
                              }}
                            />
                          ) : (
                            <Chip
                              label={feature.website}
                              size="small"
                              sx={{
                                bgcolor: feature.enabled
                                  ? 'rgba(50, 41, 84, 0.1)'
                                  : 'rgba(0, 0, 0, 0.08)',
                                color: feature.enabled ? 'primary.main' : 'text.secondary',
                              }}
                            />
                          )}
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <NotificationsIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {feature.notificationEmailType === NotificationEmailTypeEnum.USE_GLOBAL_ADDRESS
                              ? t('features_notification_type_global')
                              : feature.notificationEmailType === NotificationEmailTypeEnum.USE_CUSTOM_ADDRESS
                                ? t('features_notification_type_custom')
                                : t('features_notification_type_none')}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          startIcon={<SettingsIcon />}
                          onClick={() => handleOpenNotificationSettings(feature)}
                          disabled={!feature.enableGlobally || !!feature.emBreve}
                        >
                          {t('features_btn_configure')}
                        </Button>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          {t('features_templates_count_label', { count: feature.templates?.length ?? 0 })}
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleOpenTemplateDialog(feature)}
                          disabled={!feature.enableGlobally || !!feature.emBreve}
                        >
                          {t('features_btn_view_templates')}
                        </Button>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="text.secondary">
                          {t('features_sample_limit_label', { used: feature.dailySampleUsed, limit: feature.dailySampleLimit })}
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<MarkEmailReadIcon />}
                          onClick={() => handleDispatchSampleEmail(feature)}
                          disabled={
                            !feature.enableGlobally ||
                            !!feature.emBreve ||
                            !!dispatchingSampleById[feature.id] ||
                            (feature.dailySampleLimit > 0 && feature.dailySampleUsed >= feature.dailySampleLimit)
                          }
                        >
                          {t('features_btn_sample_email')}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

          </Paper>
        ))}

        {/* Template Change Notification Section */}
        <Paper sx={{ mb: 4, p: 3, borderRadius: 2, bgcolor: '#f8f9fa' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <NotificationsIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h6" color="primary">
              {t('features_template_changes_title')}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('features_template_changes_desc')}
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<NotificationsIcon />}
            onClick={handleNotifyTemplateChange}
            sx={{ fontWeight: 'bold' }}
          >
            {t('features_template_changes_btn_request')}
          </Button>
        </Paper>

        <Dialog
          open={sampleConfirmOpen}
          onClose={handleCloseSampleConfirmDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: dialogPaperSx }}
        >
          <DialogTitle>
            {t('features_sample_confirm_title')}
          </DialogTitle>
          <DialogContent sx={dialogContentSx}>
            <Typography variant="body1" paragraph sx={{ mb: 2 }}>
              {t('features_sample_confirm_p1', { templateName: sampleConfirmFeature?.name ?? '' })}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 2 }}>
              {t('features_sample_confirm_p2')}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 2 }}>
              {t('features_sample_confirm_p3')}
            </Typography>

            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 2, py: 1.5, mb: 2 }}>
              <FormControlLabel
                control={<Checkbox checked disabled />}
                label={t('features_sample_confirm_p2_auto', { email: userEmail || t('features_sample_confirm_unknown_email') })}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  ml: 0,
                  mr: 0,
                  '& .MuiFormControlLabel-label': {
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
              />

              <Divider sx={{ my: 1.5 }} />

              {sampleEmailOptions.length > 0 ? (
                sampleEmailOptions.map((item) => {
                  const normalizedEmail = normalizeEmailValue(item.email);
                  const checked = sampleSelectedEmails.some(email => normalizeEmailValue(email) === normalizedEmail);

                  return (
                    <FormControlLabel
                      key={normalizedEmail}
                      control={(
                        <Checkbox
                          checked={checked}
                          onChange={() => handleToggleSampleEmail(item.email)}
                          disabled={sampleConfirmLoading}
                        />
                      )}
                      label={formatSampleEmailOption(item)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        ml: 0,
                        mr: 0,
                        '& .MuiFormControlLabel-label': {
                          display: 'flex',
                          alignItems: 'center',
                        },
                      }}
                    />
                  );
                })
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('features_sample_confirm_no_extra_emails')}
                </Typography>
              )}
            </Box>

            {sampleLimitReached && (
              <MuiAlert severity="warning" variant="outlined">
                {t('features_alert_warning_sample_limit')}
              </MuiAlert>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseSampleConfirmDialog}
              disabled={sampleConfirmLoading}
            >
              {t('features_sample_confirm_btn_cancel')}
            </Button>
            <Button
              onClick={handleConfirmDispatchSampleEmail}
              variant="contained"
              disabled={sampleConfirmLoading || sampleLimitReached}
              startIcon={sampleConfirmLoading ? <CircularProgress size={18} /> : undefined}
            >
              {t('features_sample_confirm_btn_confirm')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Notification Settings Dialog */}
        <Dialog
          open={notificationDialogOpen}
          onClose={() => setNotificationDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: dialogPaperSx }}
        >
          <DialogTitle>
            {t('notif_dialog_title', { name: selectedFeature?.name })}
          </DialogTitle>
          <DialogContent sx={dialogContentSx}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Esta é uma área de gerenciamento de como serão recebidas as notificações deste recurso. Selecione entre usar o e-mail global de notificações ou definir um e-mail personalizado para esta funcionalidade.
            </Typography>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('notif_dialog_type_title')}
              </Typography>
              <RadioGroup
                value={selectedFeature?.notificationEmailType ?? NotificationEmailTypeEnum.USE_GLOBAL_ADDRESS}
                onChange={(e) => handleNotificationTypeChange(Number(e.target.value) as NotificationEmailTypeEnum)}
              >
                <FormControlLabel
                  value={NotificationEmailTypeEnum.USE_GLOBAL_ADDRESS}
                  control={<Radio />}
                  label={t('notif_dialog_option_global')}
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 1 }}>
                  {t('notif_dialog_option_global_desc')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 2 }}>
                  → <Link component={RouterLink} to="/imigra-pulse-canada/notificacoes">{t('routes_notifications_label')}</Link>
                </Typography>
                <FormControlLabel
                  value={NotificationEmailTypeEnum.USE_CUSTOM_ADDRESS}
                  control={<Radio />}
                  label={t('notif_dialog_option_custom')}
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                  {t('notif_dialog_option_custom_desc')}
                </Typography>
            </RadioGroup>
          </Box>

            {selectedFeature?.notificationEmailType === NotificationEmailTypeEnum.USE_CUSTOM_ADDRESS && selectedFeature.customContacts && (
             <ContactManager
                 title={t('notif_dialog_contact_manager_title')}
                 contacts={selectedFeature.customContacts}
                 onChange={handleContactsChange}
                allowAddWhatsApp={false}
                allowAddTelegram={false}
                whatsAppEmptyContent={<MuiAlert severity="info" variant="outlined">{t('notif_dialog_whatsapp_empty')}</MuiAlert>}
                notifyTelegram={!!selectedFeature.notifyTelegram}
                onNotifyTelegramChange={(value) => setSelectedFeature(prev => prev ? { ...prev, notifyTelegram: value } : prev)}
                telegramGroupUrl={telegramGroupUrl}
                hideTelegramSection={true}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNotificationDialogOpen(false)}>
              {t('notif_dialog_btn_cancel')}
            </Button>
            <Button onClick={handleSaveNotificationSettings} variant="contained">
              {t('notif_dialog_btn_save')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Template Preview Dialog */}
        <Dialog
          open={templateDialogOpen}
          onClose={() => setTemplateDialogOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{ sx: dialogPaperSx }}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">
                {t('template_dialog_title', { name: selectedFeature?.name })}
              </Typography>
              {selectedFeature?.templates && selectedFeature.templates.length > 1 && (
                <Typography variant="caption" color="text.secondary">
                  {t('template_dialog_pagination', { index: currentTemplateIndex + 1, total: selectedFeature.templates.length })}
                </Typography>
              )}
            </Box>
          </DialogTitle>
          <DialogContent sx={dialogContentSx}>
            <Tabs value={templateDialogTab} onChange={(_, v) => setTemplateDialogTab(v)} sx={{ mb: 2 }} aria-label="Template dialog tabs">
              <Tab label={t('template_dialog_tab_labels') || 'Legenda e instruções'} />
              <Tab label={t('template_dialog_tab_overview') || 'Templates'} />
            </Tabs>

            {/* Tab 2: Overview with images and descriptions */}
            {templateDialogTab === 1 && (
              selectedTemplate ? (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {selectedFeature?.templates && selectedFeature.templates.length > 1 && (
                      <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                        <IconButton onClick={handlePrevTemplate} size="small">
                          <ChevronLeftIcon />
                        </IconButton>
                        <IconButton onClick={handleNextTemplate} size="small">
                          <ChevronRightIcon />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('features_templates_dialog_intro')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 5, mb: 3 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {t('template_dialog_original_label')}
                      </Typography>
                      <Box sx={{ position: 'relative', width: '100%', height: 360, border: '1px solid #ddd', borderRadius: 1, overflow: 'auto', bgcolor: '#fff' }}>
                        {originalImageLoading && (
                          <Box sx={{ position: 'absolute', inset: 0 }}>
                            <Skeleton variant="rectangular" width="100%" height="100%" />
                          </Box>
                        )}
                        <CardMedia
                          component="img"
                          image={selectedTemplate.originalImage}
                          alt={t('template_dialog_original_label')}
                          onLoad={() => setOriginalImageLoading(false)}
                          onError={() => setOriginalImageLoading(false)}
                          sx={{ display: 'block' }}
                        />
                      </Box>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {t('template_dialog_param_label')}
                      </Typography>
                      <Box sx={{ position: 'relative', width: '100%', height: 360, border: '1px solid #ddd', borderRadius: 1, overflow: 'auto', bgcolor: '#fff' }}>
                        {paramImageLoading && (
                          <Box sx={{ position: 'absolute', inset: 0 }}>
                            <Skeleton variant="rectangular" width="100%" height="100%" />
                          </Box>
                        )}
                        <CardMedia
                          component="img"
                          image={selectedTemplate.parametrizedImage}
                          alt={t('template_dialog_param_label')}
                          onLoad={() => setParamImageLoading(false)}
                          onError={() => setParamImageLoading(false)}
                          sx={{ display: 'block' }}
                        />
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {t('features_templates_modal_help_p1')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('features_templates_modal_help_p2')}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                </Box>
              ) : (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('template_dialog_no_templates_msg') || 'Não há templates cadastrados no momento.'}
                  </Typography>
                  <Button variant="contained" onClick={() => { setTemplateChangeFromTemplateDialog(true); setTemplateChangeModalOpen(true); }}>
                    {t('template_dialog_no_templates_cta') || 'Cadastrar Template'}
                  </Button>
                </Box>
              )
            )}

            {/* Tab 1: Instructions + Social label input */}
            {templateDialogTab === 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Esta é uma área de gerenciamento de como serão recebidas as notificações (legendas) relacionadas ao Template selecionado.
                </Typography>
                {renderSocialLabelInstructions()}
                {renderTemplateExamplesCard()}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                    {t('social_label_title')}
                  </Typography>
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    value={socialLabelText}
                    onChange={(e) => setSocialLabelText(e.target.value.slice(0, 4000))}
                    placeholder={t('social_label_placeholder')}
                    variant="outlined"
                    sx={{
                      mb: 2,
                      '& .MuiInputBase-input': {
                        fontFamily: 'Segoe UI Emoji, Segoe UI, Roboto, Helvetica, Arial, sans-serif'
                      }
                    }}
                    inputProps={{ maxLength: 4000 }}
                    helperText={`${socialLabelText.length}/4000 caracteres`}
                  />
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={handleSaveSocialLabel}>
                      {t('social_label_btn_save')}
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTemplateDialogOpen(false)}>
              {t('template_dialog_btn_close')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Alert */}
        <Snackbar
          open={alertOpen}
          autoHideDuration={alertDuration}
          onClose={() => setAlertOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setAlertOpen(false)}
            severity={alertSeverity}
            sx={{ width: '100%' }}
          >
            {alertMessage}
          </Alert>
        </Snackbar>

        {/* Template Change Modal */}
        <TemplateChangeModal
          open={templateChangeModalOpen}
          onClose={() => setTemplateChangeModalOpen(false)}
          features={features.map(f => ({ id: f.id, name: f.name }))}
          onSubmit={handleTemplateChangeSubmit}
          initialSelectedFeatureIds={templateChangeFromTemplateDialog && selectedFeature ? [selectedFeature.id] : []}
          lockFeatureSelection={templateChangeFromTemplateDialog}
        />
      </Box>
    </MainLayout>
  );
};

export default Features;
